export type LetzteAktionenColumnKey =
    | 'aktion_id'
    | 'aktion'
    | 'status'
    | 'betrieb'
    | 'anzahl_tiere'
    | 'aktuelle_gruppe'
    | 'abschnitt'
    | 'kommentar'
    | 'herkunft'
    | 'datum'
    | 'einkaufspreis'
    | 'verkaufspreis'
    | 'erloesdifferenz'
    | 'tageszunahmen';

export interface LetzteAktionenColumnDef {
    key: LetzteAktionenColumnKey;
    label: string;
    visible: boolean;
    /** Shown in Spalten menu but values only for Ausstallen rows. */
    ausstallOnly?: boolean;
}

const LETZTE_AKTIONEN_COLUMNS_STORAGE_KEY = 'letzte_aktionen_spalten_v3';
const LETZTE_AKTIONEN_COLUMNS_STORAGE_KEY_V1 = 'letzte_aktionen_spalten_v1';
const LETZTE_AKTIONEN_COLUMNS_STORAGE_KEY_V2 = 'letzte_aktionen_spalten_v2';

const PREIS_COLUMN_KEYS: ReadonlySet<LetzteAktionenColumnKey> = new Set([
    'einkaufspreis',
    'verkaufspreis',
    'erloesdifferenz',
]);

const GEWICHT_COLUMN_KEYS: ReadonlySet<LetzteAktionenColumnKey> = new Set(['tageszunahmen']);

export function isLetzteAktionenPreisColumn(key: LetzteAktionenColumnKey): boolean {
    return PREIS_COLUMN_KEYS.has(key);
}

export function isLetzteAktionenGewichtColumn(key: LetzteAktionenColumnKey): boolean {
    return GEWICHT_COLUMN_KEYS.has(key);
}

const DEFAULT_LETZTE_AKTIONEN_COLUMNS: LetzteAktionenColumnDef[] = [
    { key: 'aktion_id', label: 'Aktion ID', visible: true },
    { key: 'aktion', label: 'Aktion', visible: true },
    { key: 'status', label: 'Status', visible: true },
    { key: 'betrieb', label: 'Betrieb', visible: true },
    { key: 'anzahl_tiere', label: 'Anzahl Tiere', visible: true },
    { key: 'aktuelle_gruppe', label: 'Aktuelle Gruppe', visible: true },
    { key: 'abschnitt', label: 'Abschnitt', visible: true },
    { key: 'kommentar', label: 'Kommentar', visible: true },
    { key: 'herkunft', label: 'Herkunft/Zielbetrieb', visible: true },
    { key: 'datum', label: 'Datum', visible: true },
    { key: 'einkaufspreis', label: 'Einkaufspreis', visible: false },
    { key: 'verkaufspreis', label: 'Verkaufspreis', visible: false, ausstallOnly: true },
    { key: 'erloesdifferenz', label: 'Erlösdifferenz pro Tag', visible: false, ausstallOnly: true },
    { key: 'tageszunahmen', label: 'Tageszunahme', visible: false, ausstallOnly: true },
];

function parseSavedVisibility(raw: string | null): Partial<Record<LetzteAktionenColumnKey, boolean>> | null {
    if (!raw) {
        return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return null;
    }
    return parsed as Partial<Record<LetzteAktionenColumnKey, boolean>>;
}

function migrateSavedVisibility(
    saved: Partial<Record<LetzteAktionenColumnKey, boolean>>,
): Partial<Record<LetzteAktionenColumnKey, boolean>> {
    const next = { ...saved };
    if (next.verkaufspreis === true && !Object.prototype.hasOwnProperty.call(saved, 'erloesdifferenz')) {
        next.erloesdifferenz = true;
    }
    if (next.verkaufspreis === true && !Object.prototype.hasOwnProperty.call(saved, 'tageszunahmen')) {
        next.tageszunahmen = true;
    }
    return next;
}

export function loadLetzteAktionenColumns(): LetzteAktionenColumnDef[] {
    try {
        const hadV3 = !!localStorage.getItem(LETZTE_AKTIONEN_COLUMNS_STORAGE_KEY);
        let saved = parseSavedVisibility(localStorage.getItem(LETZTE_AKTIONEN_COLUMNS_STORAGE_KEY));
        let migratedFromOlder = false;
        if (!saved) {
            saved =
                parseSavedVisibility(localStorage.getItem(LETZTE_AKTIONEN_COLUMNS_STORAGE_KEY_V2)) ??
                parseSavedVisibility(localStorage.getItem(LETZTE_AKTIONEN_COLUMNS_STORAGE_KEY_V1));
            if (saved) {
                saved = migrateSavedVisibility(saved);
                migratedFromOlder = true;
            }
        }
        if (!saved) {
            return DEFAULT_LETZTE_AKTIONEN_COLUMNS.map(col => ({ ...col }));
        }
        const columns = DEFAULT_LETZTE_AKTIONEN_COLUMNS.map(col => ({
            ...col,
            visible: typeof saved[col.key] === 'boolean' ? saved[col.key]! : col.visible,
        }));
        if (migratedFromOlder && !hadV3) {
            saveLetzteAktionenColumns(columns);
        }
        return columns;
    } catch {
        return DEFAULT_LETZTE_AKTIONEN_COLUMNS.map(col => ({ ...col }));
    }
}

export function saveLetzteAktionenColumns(columns: LetzteAktionenColumnDef[]): void {
    const payload: Partial<Record<LetzteAktionenColumnKey, boolean>> = {};
    for (const col of columns) {
        payload[col.key] = col.visible;
    }
    localStorage.setItem(LETZTE_AKTIONEN_COLUMNS_STORAGE_KEY, JSON.stringify(payload));
}
